import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Eye, TestTube, Loader2, CheckCircle2, XCircle, Sparkles,
  Bold, Italic, Link as LinkIcon, List, Heading2, MailCheck,
} from 'lucide-react';
import { authHeaders } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WelcomeMailEditor — one-off template that ships to every new subscriber.
 *
 * The template is a single markdown body + subject + preheader stored in
 * settings_collection under id="welcome_newsletter". A boolean `enabled`
 * switches the auto-send in `create_lead` and the lead-magnet signup on
 * or off. A separate manual "verstuur nu" endpoint powers the LeadsPanel
 * button when the admin wants to (re)send the mail to one address.
 *
 * Kept separate from the broadcast composer because welcome emails
 * behave differently: they don't pull latest content blocks, they're
 * per-recipient triggered rather than mass-scheduled, and they're the
 * subscriber's first impression — so they deserve their own editor with
 * clear "auto-send" and "dedupe log" affordances.
 */

// Self-contained styles — mirrors NewsletterPanel's local palette so the
// two editors visually feel like siblings without an import graph.
const styles = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', margin: 0, color: '#1A1918' },
  h3: { fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', margin: '0 0 0.75rem', color: '#1A1918' },
  sub: { color: '#585755', fontSize: '0.9rem', margin: '0.25rem 0 0' },
  recipientsBox: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.75rem 1rem', background: 'white', border: '1px solid #EAE5DD',
  },
  recipientsNum: { fontSize: '1.5rem', fontFamily: "'Playfair Display', serif", color: '#1A1918', lineHeight: 1 },
  recipientsLabel: { color: '#8C8B88', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase' },

  flash: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' },
  flashOk: { background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC' },
  flashError: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' },

  toggleBar: {
    display: 'flex', alignItems: 'center', gap: '0.85rem',
    padding: '0.85rem 1rem', border: '1px solid #E5DFD1',
    background: '#FAF7F0', marginBottom: '1rem', flexWrap: 'wrap',
  },
  toggleSwitch: (on) => ({
    position: 'relative', width: '46px', height: '26px',
    background: on ? '#C5A059' : '#D4CDBF',
    borderRadius: '13px', cursor: 'pointer', transition: 'background 0.15s',
    flexShrink: 0,
  }),
  toggleKnob: (on) => ({
    position: 'absolute', top: '3px', left: on ? '23px' : '3px',
    width: '20px', height: '20px', background: '#FFF',
    borderRadius: '50%', transition: 'left 0.15s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  }),
  toggleText: { flex: 1, minWidth: '200px', fontSize: '0.9rem', color: '#1A1918', lineHeight: 1.4 },
  toggleTitle: { fontWeight: 600, display: 'block' },
  toggleHint: { color: '#8C8B88', fontSize: '0.8rem' },
  savePill: { padding: '0.25rem 0.6rem', borderRadius: '4px', background: '#F3F0E7', color: '#585755', fontSize: '0.7rem' },

  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: '1.5rem', alignItems: 'start' },
  composer: { background: 'white', border: '1px solid #EAE5DD', padding: '1.5rem' },

  langRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '1rem', marginBottom: '0.25rem', flexWrap: 'wrap',
  },
  langChips: { display: 'flex', gap: '0.35rem' },
  autoLabel: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#585755' },
  segmentChip: {
    padding: '0.35rem 0.7rem', fontSize: '0.78rem',
    background: 'white', color: '#585755', border: '1px solid #E4D9C1',
    cursor: 'pointer', letterSpacing: '0.01em',
  },
  segmentChipActive: {
    background: '#1A1918', color: '#C5A059', borderColor: '#1A1918',
    fontWeight: 600,
  },

  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#1A1918', letterSpacing: '0.03em', marginTop: '1rem', marginBottom: '0.35rem' },
  input: { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #D8D3CA', fontSize: '0.95rem', background: 'white', boxSizing: 'border-box' },
  hint: { color: '#8C8B88', fontSize: '0.75rem', lineHeight: 1.55, margin: '0.35rem 0 0' },

  toolbar: { display: 'flex', gap: '0.25rem', marginBottom: '0.35rem' },
  tbBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.6rem', background: '#FAF9F6', border: '1px solid #EAE5DD', cursor: 'pointer', color: '#1A1918' },
  textarea: { width: '100%', padding: '0.75rem', border: '1px solid #D8D3CA', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.9rem', lineHeight: 1.55, boxSizing: 'border-box', resize: 'vertical' },

  actionsRow: { marginTop: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' },
  testGroup: { display: 'flex', gap: '0.5rem', flex: '1 1 260px' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1.15rem', background: '#1A1918', color: '#C5A059', border: 'none', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' },
  btnGhost: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', background: 'white', color: '#1A1918', border: '1px solid #D8D3CA', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },

  previewCol: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  previewHeader: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1A1918', fontWeight: 700 },
  previewFrame: { width: '100%', height: '520px', border: '1px solid #EAE5DD', background: 'white' },

  logSection: {
    marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #EEE8DA',
  },
  logRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.6rem 0', borderBottom: '1px solid #F5F0E5', fontSize: '0.85rem',
    gap: '1rem', flexWrap: 'wrap',
  },
  logEmail: { color: '#1A1918', fontWeight: 500 },
  logMeta: { color: '#8C8B88', fontSize: '0.75rem' },
  empty: { color: '#8C8B88', fontSize: '0.9rem' },
};

const WelcomeMailEditor = () => {
  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [body, setBody] = useState('');
  const [language, setLanguage] = useState('nl');
  const [updatedAt, setUpdatedAt] = useState(null);

  const [previewHtml, setPreviewHtml] = useState('');
  const [isPreviewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [isTesting, setTesting] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);

  const textareaRef = useRef(null);

  // -------- Load current config + log
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, logRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/newsletters/welcome`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/admin/newsletters/welcome/log?limit=20`, { headers: authHeaders() }),
        ]);
        if (cfgRes.ok) {
          const c = await cfgRes.json();
          setEnabled(!!c.enabled);
          setSubject(c.subject || '');
          setPreheader(c.preheader || '');
          setBody(c.body_markdown || '');
          setLanguage(c.language || 'nl');
          setUpdatedAt(c.updated_at || null);
        }
        if (logRes.ok) {
          const l = await logRes.json();
          setLog(l.items || []);
          setLogTotal(l.total || 0);
        }
      } catch (e) { /* silent */ }
    })();
  }, []);

  const flashOk = (text) => { setFlash({ type: 'ok', text }); setTimeout(() => setFlash(null), 4000); };
  const flashErr = (text) => { setFlash({ type: 'error', text }); setTimeout(() => setFlash(null), 6000); };

  // -------- Markdown toolbar (mirrors the broadcast composer)
  const wrapSelection = (open, close = null) => {
    const el = textareaRef.current;
    if (!el) return;
    const closer = close === null ? open : close;
    const { selectionStart, selectionEnd, value } = el;
    const before = value.slice(0, selectionStart);
    const middle = value.slice(selectionStart, selectionEnd);
    const after = value.slice(selectionEnd);
    const next = `${before}${open}${middle || ''}${closer}${after}`;
    setBody(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(selectionStart + open.length, selectionEnd + open.length);
    }, 0);
  };
  const insertLink = () => {
    const url = window.prompt('URL:', 'https://casaothers.nl/');
    if (!url) return;
    wrapSelection('[', `](${url})`);
  };

  // -------- Preview
  const runPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters/welcome/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ enabled, subject, preheader, body_markdown: body, language }),
      });
      if (!res.ok) throw new Error(await res.text());
      const d = await res.json();
      setPreviewHtml(d.html || '');
    } catch (e) {
      flashErr('Kon preview niet laden.');
    } finally {
      setPreviewLoading(false);
    }
  }, [enabled, subject, preheader, body, language]);

  // Debounced auto-preview so the admin sees the mail update while they type.
  useEffect(() => {
    const t = setTimeout(runPreview, 400);
    return () => clearTimeout(t);
  }, [runPreview]);

  // -------- Save
  const save = async () => {
    if (!subject.trim()) { flashErr('Vul een onderwerp in.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/newsletters/welcome`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ enabled, subject, preheader, body_markdown: body, language }),
      });
      if (!res.ok) throw new Error(await res.text());
      const c = await res.json();
      setUpdatedAt(c.updated_at || null);
      flashOk('Welkomstmail opgeslagen.');
    } catch (e) {
      flashErr('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  // -------- Test send
  const sendTest = async () => {
    if (!testEmail.trim() || !testEmail.includes('@')) { flashErr('Geldig test-adres invoeren.'); return; }
    setTesting(true);
    try {
      // Save first so the test hits the current version.
      await save();
      const res = await fetch(`${API_URL}/api/admin/newsletters/welcome/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ to_email: testEmail.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Send failed');
      }
      flashOk(`Test verstuurd naar ${testEmail}.`);
    } catch (e) {
      flashErr(`Test mislukt: ${e.message || e}`);
    } finally {
      setTesting(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div data-testid="welcome-mail-editor">
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.h2}>Welkomstmail</h2>
          <p style={styles.sub}>
            Automatische mail die iedere nieuwe subscriber ontvangt direct na het aanmelden
            (via aanvraagformulier of gratis download). Wordt maar één keer per e-mailadres verzonden.
          </p>
        </div>
        <div style={styles.recipientsBox} data-testid="welcome-total-sent">
          <MailCheck size={16} color="#C5A059" />
          <div>
            <div style={styles.recipientsNum}>{logTotal}</div>
            <div style={styles.recipientsLabel}>totaal verzonden</div>
          </div>
        </div>
      </div>

      {flash && (
        <div
          style={{ ...styles.flash, ...(flash.type === 'error' ? styles.flashError : styles.flashOk) }}
          data-testid={`welcome-flash-${flash.type}`}
        >
          {flash.type === 'error' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>{flash.text}</span>
        </div>
      )}

      {/* Enable/disable auto-send */}
      <div style={styles.toggleBar} data-testid="welcome-autosend-row">
        <div
          style={styles.toggleSwitch(enabled)}
          onClick={() => setEnabled(!enabled)}
          role="switch"
          aria-checked={enabled}
          data-testid="welcome-autosend-toggle"
        >
          <div style={styles.toggleKnob(enabled)} />
        </div>
        <div style={styles.toggleText}>
          <span style={styles.toggleTitle}>
            {enabled ? 'Automatisch versturen bij nieuwe aanmelding' : 'Automatisch versturen is uit'}
          </span>
          <span style={styles.toggleHint}>
            {enabled
              ? 'Elke nieuwe subscriber met GDPR-consent ontvangt deze mail direct.'
              : 'Alleen handmatig via de leadslijst of via een test-verzending.'}
          </span>
        </div>
        {updatedAt && <span style={styles.savePill}>Laatst opgeslagen {fmtDate(updatedAt)}</span>}
      </div>

      <div style={styles.layout}>
        {/* Composer */}
        <div style={styles.composer}>
          <div style={styles.langRow}>
            <span style={styles.autoLabel}>Taal</span>
            <div style={styles.langChips}>
              {[{ v: 'nl', l: 'Nederlands' }, { v: 'en', l: 'English' }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  style={{
                    ...styles.segmentChip,
                    ...(language === o.v ? styles.segmentChipActive : {}),
                  }}
                  onClick={() => setLanguage(o.v)}
                  data-testid={`welcome-lang-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          <label style={styles.label}>Onderwerp</label>
          <input
            style={styles.input}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Welkom bij CasaOthers 🇪🇸"
            data-testid="welcome-subject"
          />

          <label style={styles.label}>Preheader</label>
          <input
            style={styles.input}
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder="Fijn dat je erbij bent — dit kun je van ons verwachten."
            data-testid="welcome-preheader"
          />
          <p style={styles.hint}>
            Korte preview-tekst die naast de onderwerpregel in de inbox verschijnt.
          </p>

          <label style={styles.label}>Bericht (Markdown)</label>
          <div style={styles.toolbar}>
            <button style={styles.tbBtn} onClick={() => wrapSelection('**')} title="Vet" data-testid="welcome-tb-bold"><Bold size={14} /></button>
            <button style={styles.tbBtn} onClick={() => wrapSelection('*')} title="Cursief" data-testid="welcome-tb-italic"><Italic size={14} /></button>
            <button style={styles.tbBtn} onClick={() => wrapSelection('## ', '')} title="Kop" data-testid="welcome-tb-h2"><Heading2 size={14} /></button>
            <button style={styles.tbBtn} onClick={() => wrapSelection('- ', '')} title="Lijst" data-testid="welcome-tb-list"><List size={14} /></button>
            <button style={styles.tbBtn} onClick={insertLink} title="Link" data-testid="welcome-tb-link"><LinkIcon size={14} /></button>
          </div>
          <textarea
            ref={textareaRef}
            style={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            data-testid="welcome-body"
          />

          <div style={styles.actionsRow}>
            <button
              type="button"
              onClick={save}
              disabled={isSaving}
              style={styles.btnPrimary}
              data-testid="welcome-save"
            >
              {isSaving ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
              Opslaan
            </button>

            <div style={styles.testGroup}>
              <input
                style={{ ...styles.input, margin: 0, flex: 1, minWidth: '180px' }}
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="jouw@email.nl"
                data-testid="welcome-test-email"
              />
              <button
                type="button"
                style={styles.btnGhost}
                onClick={sendTest}
                disabled={isTesting}
                data-testid="welcome-test-send"
              >
                {isTesting ? <Loader2 size={14} className="spin" /> : <TestTube size={14} />}
                Testen
              </button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div style={styles.previewCol}>
          <div style={styles.previewHeader}>
            <Eye size={14} /> Live preview
            {isPreviewLoading && <Loader2 size={12} className="spin" />}
          </div>
          <iframe
            title="welcome-preview"
            style={styles.previewFrame}
            srcDoc={previewHtml || '<p style="font-family:sans-serif;color:#8C8B88;padding:2rem;text-align:center">Preview verschijnt zodra je typt…</p>'}
            data-testid="welcome-preview-iframe"
          />
        </div>
      </div>

      {/* Recent sends log */}
      <div style={styles.logSection}>
        <h3 style={styles.h3}>Recente verzendingen</h3>
        {log.length === 0 ? (
          <p style={styles.empty}>Nog geen welkomstmails verzonden.</p>
        ) : (
          <div data-testid="welcome-log">
            {log.map((row, idx) => (
              <div key={`${row.email}-${idx}`} style={styles.logRow}>
                <div>
                  <span style={styles.logEmail}>{row.email}</span>
                  <span style={{ ...styles.logMeta, marginLeft: '0.75rem' }}>
                    {row.source === 'lead_form' && 'via aanvraagformulier'}
                    {row.source === 'lead_magnet' && 'via gratis download'}
                    {row.source === 'manual' && 'handmatig verstuurd'}
                    {row.source === 'auto' && 'automatisch'}
                  </span>
                </div>
                <span style={styles.logMeta}>{fmtDate(row.sent_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeMailEditor;
